import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthLayout } from '@/components/layout/auth-layout';
import {
  apiClient,
  type ApiError,
  type FirstAccessPreviewResponse,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

export default function PrimeiroAcessoPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const hydrateFromTokens = useAuthStore((s) => s.hydrateFromTokens);

  const [preview, setPreview] = useState<FirstAccessPreviewResponse | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.firstAccessPreview(token);
        if (!cancelled) setPreview(res);
      } catch (err) {
        if (!cancelled) {
          const s = (err as ApiError | undefined)?.status;
          if (s === 404) setPreviewError('Convite não encontrado.');
          else if (s === 410) {
            setPreviewError(
              extractApiMessage(err, 'Convite expirou ou já foi utilizado.'),
            );
          } else {
            setPreviewError(
              extractApiMessage(err, 'Falha ao carregar convite.'),
            );
          }
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleActivate(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    if (password !== confirmPassword) {
      setActionError('As senhas não conferem.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.firstAccessActivate(token, password);
      hydrateFromTokens({
        user: res.user,
        accessToken: res.accessToken,
        tenantId: res.tenantId,
      });
      navigate('/escritorio', { replace: true });
    } catch (err) {
      const s = (err as ApiError | undefined)?.status;
      if (s === 410) {
        setActionError(
          extractApiMessage(err, 'Convite expirou ou já foi utilizado.'),
        );
      } else if (s === 404) {
        setActionError('Convite não encontrado.');
      } else {
        setActionError(extractApiMessage(err, 'Falha ao ativar conta.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (previewLoading) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Carregando convite...</CardTitle>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  if (previewError || !preview) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Convite indisponível</CardTitle>
            <CardDescription>
              {previewError ?? 'Erro desconhecido.'}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/sign-in" className="text-sm underline">
              Voltar
            </Link>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Bem-vindo ao {preview.tenantName}</CardTitle>
          <CardDescription>
            Como <strong>{preview.ownerEmail}</strong> você foi designado(a) como
            administrador(a) do escritório. Defina sua senha para ativar o
            acesso.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleActivate}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={preview.ownerEmail}
                disabled
                readOnly
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={preview.ownerName}
                disabled
                readOnly
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Pelo menos 12 caracteres, com maiúscula, minúscula e dígito.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
            </div>
            {actionError && (
              <p className="text-sm text-red-600" role="alert">
                {actionError}
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Ativando...' : 'Ativar conta e entrar'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
